import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  ClickAwayListener,
  Popper,
  Skeleton,
  Typography,
} from "@mui/material";
import { useAddendumAssociations } from "../../hooks/useAddendumAssociations";
import { handleDramaturgyTypeByFormat } from "../../shared/utils/handleDramaturgyTypeByFormat";

const isChapterOrEpisode = (typeName) =>
  typeName === "Capítulo/Episódio" || typeName === "Episódio";

const isChapterFormat = (format) =>
  handleDramaturgyTypeByFormat(format) === "Dramaturgia Longa";

const fileItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "10px 16px",
  borderBottom: "1px solid rgba(107, 108, 120, 0.2)",
  "&:last-of-type": { borderBottom: "none" },
};

const fileCapSx = {
  fontFamily: "inherit",
  fontSize: "14px",
  color: "#43444e",
  lineHeight: "16px",
  letterSpacing: "1px",
};

const fileBlockSx = {
  fontFamily: "inherit",
  fontSize: "12px",
  textTransform: "uppercase",
  border: "1px solid #8586cc",
  color: "#8586cc",
  borderRadius: "2px",
  lineHeight: "16px",
  letterSpacing: "1px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 3px 0",
};

const fileVersionSx = {
  fontFamily: "inherit",
  fontSize: "14px",
  color: "#6b6c78",
  marginLeft: "auto",
  paddingRight: "10px",
  whiteSpace: "nowrap",
};

const AttachmentDropdownItem = ({ item, format }) => {
  const typeName = item?.attachmentType?.name;
  const isChEp = isChapterOrEpisode(typeName);
  const isAdendo = typeName === "Adendo";
  const addendumId = item?.attachmentId;
  const addendumFormat = format ?? item?.format ?? item?.contentFormat;
  const isChapterAddendum = useMemo(
    () => isChapterFormat(addendumFormat),
    [addendumFormat]
  );

  const { addendumAssociations, isLoadingAddendumAssociations } =
    useAddendumAssociations({
      isAdendo,
      addendumId,
      isChapter: isChapterAddendum,
    });

  return (
    <Box component="li" sx={fileItemSx}>
      {!!item?.attachmentType && isChEp ? (
        <>
          <Typography sx={fileCapSx}>Cap {item.chapterNumber}</Typography>
          <Box sx={fileBlockSx}>Bloco {item.blockNumber}</Box>
          <Typography sx={fileVersionSx}>{item.version}º Versão</Typography>
        </>
      ) : (
        <>
          <Typography sx={fileCapSx}>{typeName}</Typography>
          {isAdendo && isLoadingAddendumAssociations && (
            <Skeleton variant="text" width={72} height={16} />
          )}
          {isAdendo &&
            !isLoadingAddendumAssociations &&
            !!addendumAssociations && (
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  color: "#666",
                  whiteSpace: "nowrap",
                }}
              >
                {addendumAssociations}
              </Typography>
            )}
          <Typography sx={fileVersionSx}>
            {isAdendo ? "-" : `${item?.version}º Versão`}
          </Typography>
        </>
      )}
    </Box>
  );
};

const AttachmentsDropdown = ({ attachments, format }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const handleToggle = useCallback((event) => {
    const trigger = event.currentTarget;
    setAnchorEl((current) => (current ? null : trigger));
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const isPlural = (value) => (value > 1 ? "s" : "");

  return (
    <Box sx={{ width: "fit-content", display: "flex" }}>
      <Box
        component="span"
        onClick={handleToggle}
        sx={{
          fontFamily: "inherit",
          fontWeight: 700,
          fontSize: "14px",
          color: "#17b4e1",
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
        }}
      >
        + {attachments.length - 1} anexo{isPlural(attachments.length - 1)}
      </Box>

      {/* Popper's flip/preventOverflow modifiers keep the popup inside the viewport
          automatically, so it no longer gets cut off near a screen edge. */}
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: 1400 }}
        modifiers={[
          { name: "offset", options: { offset: [16, 4] } },
          {
            name: "preventOverflow",
            options: { padding: 8, boundary: "clippingParents" },
          },
          { name: "flip", options: { padding: 8 } },
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box
            sx={{
              width: "240px",
              maxWidth: "calc(100vw - 16px)",
              backgroundColor: "#fff",
              borderRadius: "4px",
              boxShadow: "0 0 18px 0 rgba(0, 0, 0, 0.5)",
              overflow: "hidden",
            }}
          >
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                margin: 0,
                padding: "16px 0",
                maxHeight: "320px",
                overflowY: "auto",
              }}
            >
              {attachments.slice(1).map((item, index) => (
                <AttachmentDropdownItem
                  key={index}
                  item={item}
                  format={format}
                />
              ))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default AttachmentsDropdown;
